
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, Building2, CheckCircle2, Phone, Mail, 
  Shield, Clock, FileText, Users, Snowflake, Leaf 
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { getCommercialFocusTowns } from '@/data/townData';

const CommercialPage = () => {
  const commercialTowns = getCommercialFocusTowns();

  const commercialServices = [
    {
      title: 'Property Maintenance Contracts',
      description: 'Comprehensive grounds maintenance with predictable monthly billing. Includes mowing, edging, seasonal cleanups, and ongoing landscape care.',
      features: ['Weekly service schedules', 'Consistent crew assignments', 'Proactive site reporting', 'Emergency response'],
      icon: FileText
    },
    {
      title: 'HOA & Community Landscaping',
      description: 'Specialized care for homeowner associations, condominium complexes, and planned communities with attention to common areas and entrance features.',
      features: ['Common area maintenance', 'Entrance beautification', 'Board communication', 'Seasonal color programs'],
      icon: Users
    },
    {
      title: 'Commercial Snow Removal',
      description: 'Priority snow and ice management for businesses, ensuring safe access for customers and employees during Connecticut winters.',
      features: ['24/7 weather monitoring', 'Priority response times', 'Salting and de-icing', 'Liability documentation'],
      icon: Snowflake
    },
    {
      title: 'Corporate Campus Care',
      description: 'Professional grounds management for office parks, industrial facilities, and corporate campuses that reflects your company\'s image.',
      features: ['Image-conscious maintenance', 'Sustainable practices', 'Irrigation management', 'Enhancement programs'],
      icon: Building2
    }
  ];

  const benefits = [
    { title: 'Insured & Licensed', description: 'Full liability coverage for your protection', icon: Shield },
    { title: 'Reliable Scheduling', description: 'Consistent service you can count on', icon: Clock },
    { title: 'Detailed Reporting', description: 'Regular updates on property condition', icon: FileText },
    { title: 'Dedicated Teams', description: 'Same crew for property familiarity', icon: Users },
  ];

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LandscapingBusiness",
    "name": "PK Landscape Associates - Commercial Services",
    "description": "Commercial landscaping and property maintenance services in Fairfield County and New Haven County, CT. HOA landscaping, corporate campus care, and commercial snow removal.",
    "url": "https://pklandscapect.com/commercial",
    "telephone": "{{PHONE}}",
    "areaServed": commercialTowns.map(town => ({
      "@type": "City",
      "name": town.name
    })),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Commercial Landscaping Services",
      "itemListElement": commercialServices.map(service => ({
        "@type": "Service",
        "name": service.title,
        "description": service.description
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>Commercial Landscaping CT | HOA & Property Management Services</title>
        <meta 
          name="description" 
          content="Professional commercial landscaping in Connecticut. HOA maintenance, property management contracts, commercial snow removal & corporate campus care. Serving Fairfield & New Haven County." 
        />
        <link rel="canonical" href="https://pklandscapect.com/commercial" />
        <meta property="og:title" content="Commercial Landscaping Services | PK Landscape Associates" />
        <meta property="og:description" content="Professional commercial landscaping for businesses, HOAs, and property managers across Connecticut." />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-pkstone-900 via-pkstone-800 to-pkstone-700">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <nav className="flex items-center gap-2 text-pkstone-300 text-sm mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span>/</span>
              <span className="text-white">Commercial Services</span>
            </nav>
            
            <span className="inline-block bg-pkgreen-600 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              Commercial Landscaping
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
              Commercial Landscaping & Property Maintenance
            </h1>
            
            <p className="text-xl text-pkstone-200 mb-8 max-w-3xl leading-relaxed">
              Professional grounds management for businesses, HOAs, property managers, and commercial properties 
              throughout Fairfield County and New Haven County, Connecticut.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-pkgreen-600 text-white hover:bg-pkgreen-700 py-3 px-8 rounded-md inline-flex items-center justify-center font-medium transition-all duration-300"
              >
                Request Commercial Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="tel:2035551234"
                className="bg-white/10 border border-white/30 text-white hover:bg-white/20 py-3 px-8 rounded-md inline-flex items-center justify-center font-medium transition-all duration-300"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call (203) 555-1234
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-8 bg-pkgreen-700">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 text-white">
                <benefit.icon className="h-8 w-8 text-pkgreen-300 flex-shrink-0" />
                <div>
                  <span className="font-medium block">{benefit.title}</span>
                  <span className="text-pkgreen-200 text-sm">{benefit.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-pkstone-900 mb-4">
              Commercial Landscaping Services
            </h2>
            <p className="text-pkstone-600 max-w-2xl mx-auto">
              Comprehensive grounds management solutions designed for the unique needs of commercial properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {commercialServices.map((service, index) => (
              <div 
                key={index} 
                className="bg-pkstone-50 rounded-xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-pkgreen-100 p-3 rounded-lg">
                    <service.icon className="h-8 w-8 text-pkgreen-700" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif font-bold text-pkstone-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-pkstone-600">
                      {service.description}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-pkgreen-600 flex-shrink-0" />
                      <span className="text-pkstone-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contract Benefits */}
      <section className="py-16 bg-pkstone-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-serif font-bold text-pkstone-900 mb-6">
                Why Choose a Maintenance Contract?
              </h2>
              <p className="text-pkstone-600 mb-8">
                Our commercial maintenance contracts provide predictable budgeting, consistent service, 
                and professional property care that protects your investment and enhances your property's image.
              </p>
              
              <div className="space-y-4">
                {[
                  'Predictable monthly billing - no surprise costs',
                  'Priority response for weather events and emergencies',
                  'Consistent crew assignments for property familiarity',
                  'Proactive recommendations for property improvements',
                  'Detailed documentation for liability protection',
                  'Flexible terms to match your fiscal year'
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-pkgreen-600 flex-shrink-0 mt-0.5" />
                    <span className="text-pkstone-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-serif font-bold text-pkstone-900 mb-6">
                Request a Commercial Quote
              </h3>
              <p className="text-pkstone-600 mb-6">
                Tell us about your property and we'll provide a customized proposal tailored to your needs and budget.
              </p>
              <div className="space-y-4">
                <Link
                  to="/contact"
                  className="w-full bg-pkgreen-600 hover:bg-pkgreen-700 text-white py-3 px-6 rounded-md inline-flex items-center justify-center font-medium transition-all duration-300"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Request Proposal
                </Link>
                <a
                  href="tel:2035551234"
                  className="w-full bg-pkstone-100 hover:bg-pkstone-200 text-pkstone-900 py-3 px-6 rounded-md inline-flex items-center justify-center font-medium transition-all duration-300"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call (203) 555-1234
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-pkstone-900 mb-4">
              Commercial Service Areas
            </h2>
            <p className="text-pkstone-600 max-w-2xl mx-auto">
              We maintain commercial properties throughout Fairfield County and New Haven County.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {commercialTowns.map((town) => (
              <Link
                key={town.slug}
                to={`/locations/${town.slug}`}
                className="bg-pkstone-50 hover:bg-pkgreen-50 p-4 rounded-lg text-center transition-colors group"
              >
                <Building2 className="h-6 w-6 text-pkgreen-600 mx-auto mb-2" />
                <span className="text-pkstone-800 font-medium text-sm group-hover:text-pkgreen-700">
                  {town.name}, CT
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-16 bg-pkgreen-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif font-bold text-white mb-8">
            Industries We Serve
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              'Office Parks', 'Retail Centers', 'Medical Facilities', 'Industrial Properties',
              'HOA Communities', 'Apartment Complexes', 'Schools & Universities', 'Religious Institutions',
              'Banks & Financial', 'Restaurants & Hospitality'
            ].map((industry, i) => (
              <span 
                key={i}
                className="bg-pkgreen-700/50 text-pkgreen-100 px-4 py-2 rounded-full text-sm"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-pkstone-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif font-bold text-pkstone-900 mb-4">
            Ready to Discuss Your Commercial Property?
          </h2>
          <p className="text-pkstone-600 mb-8 max-w-2xl mx-auto">
            Contact us for a consultation. We'll evaluate your property and provide a comprehensive proposal.
          </p>
          <Link
            to="/contact"
            className="bg-pkgreen-600 hover:bg-pkgreen-700 text-white py-3 px-8 rounded-md inline-flex items-center font-medium transition-all duration-300"
          >
            Get Your Commercial Quote
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CommercialPage;
